import React, { createContext, useContext, useEffect } from 'react'
import { useWallet, type UseWalletReturn } from '@/hooks/useWallet'
import { getStoredUser } from '@/lib/api/client'

interface WalletContextType extends UseWalletReturn {
  isInitialized: boolean
}

const WalletContext = createContext<WalletContextType | undefined>(undefined)

export function WalletProvider({ children }: { children: React.ReactNode }) {
  const walletHook = useWallet()
  const [isInitialized, setIsInitialized] = React.useState(false)

  // Initialize wallet on mount if user is logged in
  useEffect(() => {
    const initializeWallet = async () => {
      try {
        const user = await getStoredUser()
        if (user?.id) {
          // Check if wallet exists and update the hasWallet state
          // This doesn't load or decrypt the wallet - just checks existence
          await walletHook.initializeWalletState()
        }
      } catch (error) {
        console.error('Error initializing wallet:', error)
      } finally {
        setIsInitialized(true)
      }
    }

    initializeWallet()
  }, [walletHook.initializeWalletState])

  return (
    <WalletContext.Provider value={{ ...walletHook, isInitialized }}>
      {children}
    </WalletContext.Provider>
  )
}

export function useWalletContext(): WalletContextType {
  const context = useContext(WalletContext)
  if (!context) {
    throw new Error('useWalletContext must be used within WalletProvider')
  }
  return context
}
